interface OTPRequest {
  timestamp: number;
}

interface RateLimitResult {
  allowed: boolean;
  remaining: number;
  resetTime: number;
  message?: string;
}

interface TranslationFunction {
  (key: string, params?: Record<string, string | number>): string;
}

const FIVE_MINUTES = 5 * 60 * 1000; // 5 minutes in milliseconds
const ONE_DAY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MAX_PER_5MIN = 2;
const MAX_PER_DAY = 5; // 5 requests per day

const STORAGE_KEY = 'jrlk';

/**
 * Get OTP requests from localStorage
 */
const getOTPRequests = (): OTPRequest[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading OTP requests from localStorage:', error);
    return [];
  }
};

/**
 * Save OTP requests to localStorage
 */
const saveOTPRequests = (requests: OTPRequest[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(requests));
  } catch (error) {
    console.error('Error saving OTP requests to localStorage:', error);
  }
};

/**
 * Clean up expired requests
 */
const cleanupExpiredRequests = (requests: OTPRequest[]): OTPRequest[] => {
  const now = Date.now();
  return requests.filter(request => {
    // Keep requests from the last 24 hours
    return now - request.timestamp < ONE_DAY;
  });
};

/**
 * Check if OTP request is allowed based on rate limits (global per device)
 */
export const checkOTPRateLimit = (t?: TranslationFunction): RateLimitResult => {
  const now = Date.now();
  let requests = getOTPRequests();
  
  // Clean up expired requests first
  requests = cleanupExpiredRequests(requests);
  
  // Check 5-minute limit (global per device)
  const recentRequests = requests.filter(req => now - req.timestamp < FIVE_MINUTES);
  
  if (recentRequests.length >= MAX_PER_5MIN) {
    const oldestRecentRequest = Math.min(...recentRequests.map(req => req.timestamp));
    const resetTime = oldestRecentRequest + FIVE_MINUTES;
    const minutes = Math.ceil((resetTime - now) / 60000);
    
    return {
      allowed: false,
      remaining: 0,
      resetTime,
      message: t ? t('auth.otpRateLimit.tooManyRequests') + '. ' + t('auth.otpRateLimit.waitMinutes', { minutes }) : 
        `Too many requests. Please wait ${minutes} minutes before trying again.`
    };
  }
  
  // Check daily limit (global per device)
  const dailyRequests = requests.filter(req => now - req.timestamp < ONE_DAY);
  
  if (dailyRequests.length >= MAX_PER_DAY) {
    const oldestDailyRequest = Math.min(...dailyRequests.map(req => req.timestamp));
    const resetTime = oldestDailyRequest + ONE_DAY;
    const timeRemaining = resetTime - now;
    const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutes = Math.ceil((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
    
    return {
      allowed: false,
      remaining: 0,
      resetTime,
      message: t ? t('auth.otpRateLimit.dailyLimitReached') + '. ' + t('auth.otpRateLimit.waitHoursMinutes', { hours, minutes }) :
        `Daily limit reached. Please wait ${hours}h ${minutes}m before trying again.`
    };
  }
  
  // Calculate remaining requests
  const remaining5Min = MAX_PER_5MIN - recentRequests.length;
  const remainingDaily = MAX_PER_DAY - dailyRequests.length;
  const remaining = Math.min(remaining5Min, remainingDaily);
  
  return {
    allowed: true,
    remaining,
    resetTime: now + FIVE_MINUTES
  };
};

/**
 * Record an OTP request (global per device)
 */
export const recordOTPRequest = (): void => {
  const now = Date.now();
  let requests = getOTPRequests();
  
  // Clean up expired requests
  requests = cleanupExpiredRequests(requests);
  
  // Add new request
  requests.push({
    timestamp: now
  });
  
  // Save updated requests
  saveOTPRequests(requests);
};

/**
 * Get remaining requests (global per device)
 */
export const getRemainingRequests = (): { remaining5Min: number; remainingDaily: number } => {
  const now = Date.now();
  let requests = getOTPRequests();
  
  // Clean up expired requests
  requests = cleanupExpiredRequests(requests);
  
  // Calculate remaining requests (global per device)
  const recentRequests = requests.filter(req => now - req.timestamp < FIVE_MINUTES);
  const dailyRequests = requests.filter(req => now - req.timestamp < ONE_DAY);
  
  return {
    remaining5Min: Math.max(0, MAX_PER_5MIN - recentRequests.length),
    remainingDaily: Math.max(0, MAX_PER_DAY - dailyRequests.length)
  };
};

/**
 * Clear all OTP requests (useful for testing or admin purposes)
 */
export const clearOTPRequests = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing OTP requests from localStorage:', error);
  }
};

/**
 * Get formatted time until reset
 */
export const getTimeUntilReset = (resetTime: number): string => {
  const now = Date.now();
  const diff = resetTime - now;
  
  if (diff <= 0) {
    return 'now';
  }
  
  const minutes = Math.ceil(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  
  return `${minutes}m`;
}; 